import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

export interface PerformanceMetric {
  name: string;
  value: number;
  timestamp: number;
}

export interface ApiMetric {
  url: string;
  duration: number;
  success: boolean;
  timestamp: number;
}

export const usePerformanceStore = defineStore('performance', () => {
  // 性能指标
  const pageLoadTime = ref(0);
  const apiMetrics = ref<ApiMetric[]>([]);
  const renderMetrics = ref<PerformanceMetric[]>([]);
  const memoryMetrics = ref<PerformanceMetric[]>([]);

  // 定时器和动画帧ID，用于清理
  let memoryMonitorInterval: number | null = null;
  let fpsAnimationFrameId: number | null = null;
  let performanceObserver: PerformanceObserver | null = null;

  // 统计数据
  const totalApiCalls = computed(() => apiMetrics.value.length);
  const successfulApiCalls = computed(() =>
    apiMetrics.value.filter(m => m.success).length
  );
  const failedApiCalls = computed(() =>
    apiMetrics.value.filter(m => !m.success).length
  );
  const averageApiTime = computed(() => {
    if (apiMetrics.value.length === 0) return 0;
    const sum = apiMetrics.value.reduce((acc, m) => acc + m.duration, 0);
    return Math.round(sum / apiMetrics.value.length);
  });
  const successRate = computed(() => {
    if (totalApiCalls.value === 0) return 100;
    return Math.round((successfulApiCalls.value / totalApiCalls.value) * 100);
  });

  // 初始化页面加载时间
  function initPageLoadTime() {
    if (typeof window !== 'undefined' && window.performance) {
      window.addEventListener('load', () => {
        setTimeout(() => {
          const perfData = window.performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
          if (perfData) {
            pageLoadTime.value = Math.round(perfData.loadEventEnd - perfData.fetchStart);
          }
        }, 0);
      });
    }
  }

  // 记录 API 调用
  function recordApiCall(url: string, duration: number, success: boolean) {
    const metric: ApiMetric = {
      url,
      duration,
      success,
      timestamp: Date.now(),
    };
    apiMetrics.value.push(metric);

    // 只保留最近 100 条记录
    if (apiMetrics.value.length > 100) {
      apiMetrics.value = apiMetrics.value.slice(-100);
    }
  }

  // 记录渲染性能
  function recordRenderMetric(name: string, value: number) {
    const metric: PerformanceMetric = {
      name,
      value,
      timestamp: Date.now(),
    };
    renderMetrics.value.push(metric);

    // 只保留最近 50 条记录
    if (renderMetrics.value.length > 50) {
      renderMetrics.value = renderMetrics.value.slice(-50);
    }
  }

  // 记录内存使用
  function recordMemoryMetric() {
    if (typeof window !== 'undefined' && (performance as any).memory) {
      const memory = (performance as any).memory;
      const usedMB = Math.round(memory.usedJSHeapSize / 1024 / 1024);
      const totalMB = Math.round(memory.totalJSHeapSize / 1024 / 1024);

      const metric: PerformanceMetric = {
        name: `内存使用 ${usedMB}MB / ${totalMB}MB`,
        value: usedMB,
        timestamp: Date.now(),
      };
      memoryMetrics.value.push(metric);

      // 只保留最近 50 条记录
      if (memoryMetrics.value.length > 50) {
        memoryMetrics.value = memoryMetrics.value.slice(-50);
      }
    }
  }

  // 获取性能报告
  function getPerformanceReport() {
    return {
      pageLoadTime: pageLoadTime.value,
      apiCalls: {
        total: totalApiCalls.value,
        successful: successfulApiCalls.value,
        failed: failedApiCalls.value,
        averageTime: averageApiTime.value,
        successRate: successRate.value,
      },
      recentApiCalls: apiMetrics.value.slice(-10),
      renderMetrics: renderMetrics.value.slice(-10),
      memoryMetrics: memoryMetrics.value.slice(-10),
    };
  }

  // 清除性能数据
  function clearMetrics() {
    apiMetrics.value = [];
    renderMetrics.value = [];
    memoryMetrics.value = [];
  }

  // 清理所有监控资源
  function cleanup() {
    stopFPSMonitoring();
    stopLongTaskMonitoring();
    if (memoryMonitorInterval !== null) {
      clearInterval(memoryMonitorInterval);
      memoryMonitorInterval = null;
    }
    clearMetrics();
  }

  // 监控 FPS
  function startFPSMonitoring() {
    if (typeof window === 'undefined') return;

    let frameCount = 0;
    let lastTime = performance.now();

    function measureFPS() {
      frameCount++;
      const currentTime = performance.now();
      const duration = currentTime - lastTime;

      if (duration >= 1000) {
        const fps = Math.round((frameCount * 1000) / duration);
        recordRenderMetric('FPS', fps);
        frameCount = 0;
        lastTime = currentTime;
      }

      fpsAnimationFrameId = requestAnimationFrame(measureFPS);
    }

    fpsAnimationFrameId = requestAnimationFrame(measureFPS);
  }

  // 停止 FPS 监控
  function stopFPSMonitoring() {
    if (fpsAnimationFrameId !== null) {
      cancelAnimationFrame(fpsAnimationFrameId);
      fpsAnimationFrameId = null;
    }
  }

  // 监控长任务
  function startLongTaskMonitoring() {
    if (typeof window === 'undefined' || !window.PerformanceObserver) return;

    performanceObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry) => {
        if (entry.duration > 50) {
          // 记录超过 50ms 的长任务
          recordRenderMetric(
            `长任务 (${Math.round(entry.duration)}ms)`,
            entry.duration
          );
        }
      });
    });

    try {
      performanceObserver.observe({ entryTypes: ['longtask'] });
    } catch (e) {
      console.warn('Long task monitoring not supported');
    }
  }

  // 停止长任务监控
  function stopLongTaskMonitoring() {
    if (performanceObserver) {
      performanceObserver.disconnect();
      performanceObserver = null;
    }
  }

  // 初始化性能监控
  function init() {
    initPageLoadTime();
    startFPSMonitoring();
    startLongTaskMonitoring();

    // 每 5 秒记录一次内存使用
    if (typeof window !== 'undefined') {
      memoryMonitorInterval = window.setInterval(() => {
        recordMemoryMetric();
      }, 5000);
    }
  }

  return {
    pageLoadTime,
    apiMetrics,
    renderMetrics,
    memoryMetrics,
    totalApiCalls,
    successfulApiCalls,
    failedApiCalls,
    averageApiTime,
    successRate,
    init,
    recordApiCall,
    recordRenderMetric,
    recordMemoryMetric,
    getPerformanceReport,
    clearMetrics,
    cleanup,
    startFPSMonitoring,
    stopFPSMonitoring,
    startLongTaskMonitoring,
    stopLongTaskMonitoring,
  };
});
